import { reactive } from 'vue';

export const supportedLocales = ['zh', 'en'];

const defaultLocale = 'zh';
const storageKey = 'wgg-locale';

function getInitialLocale() {
  if (typeof window === 'undefined') {
    return defaultLocale;
  }

  const storedLocale = window.localStorage.getItem(storageKey);

  return supportedLocales.includes(storedLocale) ? storedLocale : defaultLocale;
}

export const languageState = reactive({
  locale: getInitialLocale(),
});

if (typeof document !== 'undefined') {
  document.documentElement.lang = languageState.locale === 'zh' ? 'zh-CN' : 'en';
}

export function setLocale(locale) {
  if (!supportedLocales.includes(locale)) {
    return;
  }

  languageState.locale = locale;

  if (typeof window !== 'undefined') {
    window.localStorage.setItem(storageKey, locale);
    document.documentElement.lang = locale === 'zh' ? 'zh-CN' : 'en';
  }
}

export function getLocalized(source, locale = languageState.locale) {
  return source[locale] || source[defaultLocale] || source.en;
}

export const appCopy = {
  zh: {
    nav: {
      home: '主页',
      about: '详情',
      profile: '个人简介',
      portfolio: '编程项目',
      blog: '摄影分享',
      language: 'EN',
      languageLabel: 'Switch to English',
    },
    routeTitles: {
      home: '主页',
      about: '详情',
      profile: '个人简介',
      portfolio: '编程项目',
      blog: '摄影分享',
      article: '文章',
      index: 'Index',
      root: 'WGG',
    },
    home: {
      greeting: "Hello , I'm",
      roles: ['Web Developer', 'Photographer', 'Explorer', 'Drone Controllers', 'Music-lover'],
    },
    about: {
      heading: 'About Me',
      skillsTitle: 'Skills',
    },
    portfolio: {
      heading: 'Personal project',
      subtitle: "Explore the open source projects I've worked on so far",
      sourceTitle: '查看源码',
      demoTitle: '查看',
      items: [
        { name: 'Personal Website', status: '个人简介站.' },
        { name: 'WGG BLOG', status: '一个基于 VitePress 的个性化文档库' },
        { name: 'MP_V2（个人中后台管理系统）', status: '基于 AntDesign Pro 的个人管理系统，包含 todoList 等多种功能' },
        { name: '商城', status: '基于 Vue 和 Spring Boot 的前后端分离电商平台型项目，全套免费开源代码，收到大量学生朋友的正反馈' },
        { name: 'Github', status: 'Github 主页运营和搭建' },
        { name: 'API中转站置信度测试', status: '面向 API 中转站的 LLM 置信度测试工具，用于验证接口响应和模型输出稳定性。' },
        { name: 'SBTI人格测试', status: '一个在线人格测试项目，提供轻量化题目交互和结果展示。' },
        { name: 'SBTI人格测试Wiki结果', status: 'SBTI 人格测试的结果 Wiki，整理不同人格类型的说明和解读内容。' },
        { name: 'OpenCV', status: '基于 OpenCV 的图像处理实验项目，探索视觉识别和图像分析能力。' },
        { name: '家庭猫咪', status: '围绕家庭猫咪主题开发的趣味项目，记录和呈现猫咪相关互动内容。' },
        { name: 'Mac状态栏工具', status: '一个 macOS 状态栏工具，用于提升桌面上的日常操作效率。' },
        { name: '喂养母婴助手', status: 'iOS 母婴喂养助手，帮助记录宝宝喂养、护理和成长相关信息。' },
        { name: '口语练习助手', status: 'iOS 口语练习应用，帮助用户进行日常英语表达和发音训练。' },
        { name: '足迹记录', status: 'iOS 足迹记录应用，用于保存旅行、生活地点和移动轨迹。' },
        { name: '倒数日', status: 'iOS 倒数日应用，用于管理重要日期、纪念日和待办提醒。' },
        { name: '口袋街机', status: 'iOS 休闲游戏项目，提供轻量化的掌上街机娱乐体验。' },
        { name: 'ERP管理系统', status: '基于 Vue 的 ERP 管理系统，覆盖业务数据、图标和后台管理场景。' },
        { name: 'SMI网站', status: '基于 Next.js 搭建的 SMI 网站，用于品牌展示和信息发布。' },
        { name: '南京河西外国语国际部官网', status: '基于 Next.js 搭建的南京河西外国语国际部官网，面向学校品牌展示、课程介绍和招生信息发布。' },
        { name: '力邁學校网站', status: '基于 Next.js 搭建的力邁學校网站，用于学校形象展示、课程内容呈现和咨询入口承接。' },
        { name: '北京理工大学A-Level网站', status: '基于 Next.js 搭建的北京理工大学 A-Level 网站，集中展示国际课程、项目优势和招生资讯。' },
        { name: '漳州康桥学校国际部网站', status: '基于 Next.js 搭建的漳州康桥学校国际部网站，用于国际课程展示和学校信息发布。' },
        { name: '深圳市云顶学校网站', status: '基于 Next.js 搭建的深圳市云顶学校网站，呈现学校概况、课程体系和招生相关内容。' },
        { name: '珠海之星学校网站', status: '基于 Next.js 搭建的珠海之星学校网站，用于品牌展示、课程介绍和家校沟通入口。' },
        { name: '宁波经理学院国际课程中心', status: '基于 Next.js 搭建的宁波经理学院国际课程中心网站，展示国际课程项目和升学服务信息。' },
        { name: '佛山南海湾实验中学网站', status: '基于 Next.js 搭建的佛山南海湾实验中学网站，用于学校介绍、课程展示和招生信息发布。' },
      ],
    },
    blog: {
      tagline: '让我们一起分享美好瞬间',
      topicsTitle: 'Topics',
      articles: [
        { title: '非常棒的张杰人像', desc: '非常棒的张杰人像', slug: '非常棒的张杰人像', content: '非常棒的张杰人像' },
        { title: '温暖的花和阳光', desc: '温暖的花和阳光', slug: '温暖的花和阳光', content: '温暖的花和阳光' },
        { title: '晴朗干净的天空、云和阳光', desc: '晴朗干净的天空、云和阳光', slug: '晴朗干净的天空、云和阳光', content: '晴朗干净的天空、云和阳光' },
        { title: '无限好的夕阳色', desc: '无限好的夕阳色', slug: '无限好的夕阳色', content: '无限好的夕阳色' },
        { title: '令人神往的月亮', desc: '令人神往的月亮', slug: '令人神往的月亮', content: '令人神往的月亮' },
      ],
    },
    contact: {
      heading: 'Contact Me',
      phone: 'phone_手机号',
      wechat: 'wechat_微信',
      douyinAction: '点击直达',
      bilibiliAction: '点击直达',
      instagramAction: '点击直达',
    },
    article: {
      publishedAt: 'Published at',
    },
  },
  en: {
    nav: {
      home: 'Home',
      about: 'About',
      profile: 'Profile',
      portfolio: 'Projects',
      blog: 'Photos',
      language: '中',
      languageLabel: '切换到中文',
    },
    routeTitles: {
      home: 'Home',
      about: 'About',
      profile: 'Profile',
      portfolio: 'Projects',
      blog: 'Photos',
      article: 'Article',
      index: 'Index',
      root: 'WGG',
    },
    home: {
      greeting: "Hello , I'm",
      roles: ['Web Developer', 'Photographer', 'Explorer', 'Drone Pilot', 'Music Lover'],
    },
    about: {
      heading: 'About Me',
      skillsTitle: 'Skills',
    },
    portfolio: {
      heading: 'Personal Projects',
      subtitle: "Explore the open source projects I've worked on so far",
      sourceTitle: 'View source',
      demoTitle: 'View demo',
      items: [
        { name: 'Personal Website', status: 'A personal profile website.' },
        { name: 'WGG BLOG', status: 'A personalized documentation library built with VitePress.' },
        { name: 'MP_V2 Personal Admin System', status: 'A personal admin system based on Ant Design Pro, including todo lists and other workflows.' },
        { name: 'Mall Platform', status: 'A free open-source front-end and back-end e-commerce project built with Vue and Spring Boot.' },
        { name: 'Github', status: 'Github profile operation and setup.' },
        { name: 'API Relay Confidence Test', status: 'An LLM confidence testing tool for API relay services, built to validate API responses and model output stability.' },
        { name: 'SBTI Personality Test', status: 'An online personality test with lightweight question flows and result presentation.' },
        { name: 'SBTI Personality Wiki Results', status: 'A wiki-style result library for the SBTI personality test, organizing explanations for different personality types.' },
        { name: 'OpenCV', status: 'An OpenCV image-processing experiment exploring visual recognition and image analysis workflows.' },
        { name: 'Family Cat', status: 'A playful family-cat themed project for presenting cat-related records and interactions.' },
        { name: 'Mac Menu Bar Tool', status: 'A macOS menu bar utility designed to improve everyday desktop productivity.' },
        { name: 'Mother and Baby Feeding Assistant', status: 'An iOS assistant for tracking baby feeding, care, and growth information.' },
        { name: 'Speaking Practice Assistant', status: 'An iOS speaking practice app for everyday English expression and pronunciation training.' },
        { name: 'Footprint Journal', status: 'An iOS footprint journal for saving travel, life locations, and movement traces.' },
        { name: 'Countdown Days', status: 'An iOS countdown app for managing important dates, anniversaries, and reminders.' },
        { name: 'Pocket Arcade', status: 'An iOS casual game project that brings lightweight arcade-style play to mobile.' },
        { name: 'ERP Management System', status: 'A Vue-based ERP management system covering business data, icons, and admin workflows.' },
        { name: 'SMI Website', status: 'A Next.js website for SMI brand presentation and information publishing.' },
        { name: 'Nanjing Hexi Foreign Languages International Division Website', status: 'A Next.js official website for the Nanjing Hexi Foreign Languages International Division, covering school branding, curriculum highlights, and admissions information.' },
        { name: 'Limai School Website', status: 'A Next.js school website for Limai, designed for brand presentation, curriculum content, and inquiry conversion.' },
        { name: 'BIT A-Level Website', status: 'A Next.js website for the Beijing Institute of Technology A-Level program, presenting international curriculum details, program strengths, and admissions updates.' },
        { name: 'Zhangzhou Kang Chiao School International Division Website', status: 'A Next.js website for the Zhangzhou Kang Chiao School International Division, focused on international curriculum presentation and school information.' },
        { name: 'Shenzhen Yunding School Website', status: 'A Next.js school website presenting the school profile, curriculum system, and admissions-related content.' },
        { name: 'Zhuhai Star School Website', status: 'A Next.js school website for brand presentation, curriculum introduction, and family-school communication entry points.' },
        { name: 'Ningbo Managers College International Curriculum Center', status: 'A Next.js website for the Ningbo Managers College International Curriculum Center, presenting international programs and progression support.' },
        { name: 'Foshan Nanhaiwan Experimental High School Website', status: 'A Next.js school website for school introduction, curriculum presentation, and admissions information.' },
      ],
    },
    blog: {
      tagline: 'Share beautiful moments together',
      topicsTitle: 'Topics',
      articles: [
        { title: 'A strong portrait session for Zhang Jie', desc: 'A strong portrait session for Zhang Jie', slug: 'zhang-jie-portrait', content: 'A strong portrait session for Zhang Jie' },
        { title: 'Warm flowers and sunlight', desc: 'Warm flowers and sunlight', slug: 'warm-flowers-sunlight', content: 'Warm flowers and sunlight' },
        { title: 'Clear sky, clouds, and sunlight', desc: 'Clear sky, clouds, and sunlight', slug: 'clear-sky-clouds-sunlight', content: 'Clear sky, clouds, and sunlight' },
        { title: 'A rich sunset color', desc: 'A rich sunset color', slug: 'sunset-color', content: 'A rich sunset color' },
        { title: 'A fascinating moon', desc: 'A fascinating moon', slug: 'fascinating-moon', content: 'A fascinating moon' },
      ],
    },
    contact: {
      heading: 'Contact Me',
      phone: 'Phone',
      wechat: 'WeChat',
      douyinAction: 'open',
      bilibiliAction: 'open',
      instagramAction: 'open',
    },
    article: {
      publishedAt: 'Published at',
    },
  },
};
